export interface CornerPoint {
  x: number;
  y: number;
}

export interface Corners {
  topLeft: CornerPoint;
  topRight: CornerPoint;
  bottomLeft: CornerPoint;
  bottomRight: CornerPoint;
}

export type EnhanceMode = 'color' | 'gray' | 'scan';

const CORNER_INSET_RATIO = 0.08;

/** Fallback-Eckpunkte (Bildrand mit kleinem Einzug, damit die Ziehpunkte greifbar bleiben), falls keine Kontur erkannt wurde. */
export function fallbackCorners(width: number, height: number): Corners {
  const insetX = width * CORNER_INSET_RATIO;
  const insetY = height * CORNER_INSET_RATIO;
  return {
    topLeft: { x: insetX, y: insetY },
    topRight: { x: width - insetX, y: insetY },
    bottomLeft: { x: insetX, y: height - insetY },
    bottomRight: { x: width - insetX, y: height - insetY },
  };
}

export function scaleCorners(corners: Corners, scaleX: number, scaleY: number): Corners {
  const scale = (p: CornerPoint) => ({ x: p.x * scaleX, y: p.y * scaleY });
  return {
    topLeft: scale(corners.topLeft),
    topRight: scale(corners.topRight),
    bottomLeft: scale(corners.bottomLeft),
    bottomRight: scale(corners.bottomRight),
  };
}

/**
 * Läuft bewusst auf einem verkleinerten Bild (Performance) - `jscanify`
 * nutzt Canny-Kantenerkennung + Otsu-Schwellenwert + Konturerkennung, um das
 * größte (quasi-)rechteckige Objekt im Foto zu finden (das Blatt Papier).
 * Liefert `null`, wenn nichts Eindeutiges gefunden wird (z.B. zu wenig
 * Kontrast zum Untergrund) - der Aufrufer nutzt dann `fallbackCorners`.
 */
export function detectInitialCorners(cv: any, scanner: any, canvas: HTMLCanvasElement): Corners | null {
  const img = cv.imread(canvas);
  try {
    const contour = scanner.findPaperContour(img);
    if (!contour) return null;
    try {
      const pts = scanner.getCornerPoints(contour);
      if (!pts?.topLeftCorner || !pts.topRightCorner || !pts.bottomLeftCorner || !pts.bottomRightCorner) return null;
      return {
        topLeft: pts.topLeftCorner,
        topRight: pts.topRightCorner,
        bottomLeft: pts.bottomLeftCorner,
        bottomRight: pts.bottomRightCorner,
      };
    } finally {
      contour.delete();
    }
  } finally {
    img.delete();
  }
}

function distance(a: CornerPoint, b: CornerPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Zielgröße aus den (ggf. von Hand korrigierten) Eckpunkten ableiten statt eines festen Seitenverhältnisses - vermeidet Verzerrung des Ergebnisses. */
export function computeOutputSize(corners: Corners): { width: number; height: number } {
  const width = Math.max(distance(corners.topLeft, corners.topRight), distance(corners.bottomLeft, corners.bottomRight));
  const height = Math.max(distance(corners.topLeft, corners.bottomLeft), distance(corners.topRight, corners.bottomRight));
  return { width: Math.max(Math.round(width), 100), height: Math.max(Math.round(height), 100) };
}

export function warpToCorners(scanner: any, sourceCanvas: HTMLCanvasElement, corners: Corners, width: number, height: number): HTMLCanvasElement {
  return scanner.extractPaper(sourceCanvas, width, height, {
    topLeftCorner: corners.topLeft,
    topRightCorner: corners.topRight,
    bottomLeftCorner: corners.bottomLeft,
    bottomRightCorner: corners.bottomRight,
  });
}

/** Nächste ungerade Zahl (adaptiveThreshold verlangt eine ungerade Blockgröße), nach unten/oben auf `min`/`max` begrenzt. */
function oddClamp(n: number, min: number, max: number): number {
  const clamped = Math.min(max, Math.max(min, Math.round(n)));
  return clamped % 2 === 0 ? clamped + 1 : clamped;
}

/**
 * "Scan"-Modus nutzt einen ADAPTIVEN (lokalen) statt globalen Schwellenwert.
 * Handyfotos von Papier haben durch Blitzlicht/Schattenwurf oft ungleichmäßige
 * Ausleuchtung - ein globaler Schwarz-Weiß-Schnitt würde auf der dunkleren
 * Seite Text absaufen lassen oder auf der helleren Seite ausbleichen; der
 * adaptive Schwellenwert gleicht das lokal aus (klassische Scanner-App-Optik).
 */
export function applyEnhancement(cv: any, sourceCanvas: HTMLCanvasElement, mode: EnhanceMode): HTMLCanvasElement {
  if (mode === 'color') return sourceCanvas;

  const src = cv.imread(sourceCanvas);
  const gray = new cv.Mat();
  const outputCanvas = document.createElement('canvas');
  try {
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    if (mode === 'gray') {
      cv.imshow(outputCanvas, gray);
    } else {
      // Ein Medianfilter vor dem Schwellenwert glättet Kompressions-/Sensorrauschen,
      // das sonst feine Ziffernstriche unterbricht oder verschmilzt - ohne ihn
      // kann z.B. eine "6" durch das lokale Binarisieren so verschmelzen, dass
      // sie wie eine "8" aussieht (beobachteter Fall: "11.96" -> "11.98" bei der
      // Google-Vision-Erkennung).
      const blurred = new cv.Mat();
      const thresh = new cv.Mat();
      const antialiased = new cv.Mat();
      const softened = new cv.Mat();
      try {
        cv.medianBlur(gray, blurred, 3);
        // Blockgröße relativ zur tatsächlichen (Eingabe-)Bildbreite statt eines
        // festen Pixelwerts - eine feste "45" war ursprünglich gegen ein rund
        // 2000px breites Quellbild kalibriert (löste das "6"->"8"-Problem oben),
        // wirkte bei kleineren zugeschnittenen Bereichen aber real GRÖBER (der
        // Block deckte relativ mehr der Seite ab) und riss dadurch kleine Zeilen
        // wie das Bemerkungsfeld ganz unten komplett weg. Das gleiche
        // Größenverhältnis (45px bei 2000px Breite) bleibt erhalten, skaliert
        // aber mit - bei höherer Auflösung (jetzt bis 2800px statt 2000px
        // Quellbild) bekommt feiner Text automatisch mehr tatsächliche Pixel.
        const blockSize = oddClamp((gray.cols * 45) / 2000, 25, 91);
        cv.adaptiveThreshold(blurred, thresh, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, blockSize, 15);
        // Reine 0/255-Kanten sehen bei hoher Auflösung "treppenstufig"/pixelig
        // aus (genau das gemeldete Problem) und sind für OCR unnötig hart -
        // ein minimaler 3x3-Weichzeichner rundet die Kanten auf ein paar
        // Zwischen-Graustufen ab, ohne einzelne Striche/Ziffern zu verschmelzen.
        cv.GaussianBlur(thresh, antialiased, new cv.Size(3, 3), 0);
        // Reines 0/255 wirkt neben echtem Papierweiß grell (blendend helles
        // Weiß, hart abgeschnittenes Schwarz). Leichtes Zusammenziehen auf
        // ~8/243 nimmt der Optik die Härte, ohne den Kontrast für Menschen
        // oder OCR spürbar zu verschlechtern.
        antialiased.convertTo(softened, cv.CV_8U, 0.92, 8);
        cv.imshow(outputCanvas, softened);
      } finally {
        blurred.delete();
        thresh.delete();
        antialiased.delete();
        softened.delete();
      }
    }
  } finally {
    src.delete();
    gray.delete();
  }
  return outputCanvas;
}
